import Logo from '@/assets/logo-long-dark.svg';
import clsx from 'clsx';
import {
  BarChart3,
  ChevronLeft,
  LayoutGrid,
  LogOut,
  Map,
  UserCircle,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { featureFlags } from '../../config/featureFlags';
import { useAuth } from '../../context/AuthContext';
import PoolIcon from './PoolIcon';

interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', to: '/', icon: <LayoutGrid /> },
  ...(featureFlags.pools
    ? [{ label: 'Pools', to: '/pools', icon: <PoolIcon /> }]
    : []),
  { label: 'Customers', to: '/customers', icon: <Users /> },
  { label: 'Routes', to: '/routes', icon: <Map /> },
  { label: 'Team', to: '/team', icon: <UserCircle /> },
  { label: 'Reports', to: '/reports', icon: <BarChart3 /> },
];

const initialsOf = (name?: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AppSidebar = () => {
  const { user, company, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <aside
      className={clsx(
        'flex-shrink-0 flex flex-col bg-white border-r border-grey3 transition-[width] duration-150',
        collapsed ? 'w-16' : 'w-60'
      )}
    >
      <div
        className={clsx(
          'flex items-center border-b border-grey3 p-4',
          collapsed ? 'justify-center' : 'justify-between'
        )}
      >
        {!collapsed && (
          <Logo className="h-8 max-w-[160px] w-auto flex-shrink" />
        )}
        <button
          type="button"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={() => setCollapsed((prev) => !prev)}
          className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-light hover:bg-grey4 hover:text-primary transition-colors"
        >
          <ChevronLeft
            className={clsx(
              'w-4 h-4 transition-transform',
              collapsed && 'rotate-180'
            )}
          />
        </button>
      </div>

      {!collapsed && company && (
        <div className="mx-3 mt-4 flex items-center gap-2 bg-cloud rounded-xl px-3 py-2.5">
          <span className="flex-shrink-0 w-6 h-6 rounded-lg bg-primary text-white text-[10px] font-bold flex items-center justify-center">
            {initialsOf(company.name)}
          </span>
          <span className="text-xs font-semibold text-primary truncate">
            {company.name}
          </span>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto p-3">
        {!collapsed && (
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-grey10 px-3 pb-2.5">
            Workspace
          </p>
        )}
        <ul className="flex flex-col gap-1.5 list-none">
          {NAV_ITEMS.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === '/'}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 rounded-xl text-sm font-medium transition-colors',
                    collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5',
                    isActive
                      ? 'bg-primary text-white font-semibold'
                      : 'text-light hover:bg-cloud'
                  )
                }
              >
                <span className="w-[19px] h-[19px] flex-shrink-0 [&>svg]:w-[19px] [&>svg]:h-[19px]">
                  {item.icon}
                </span>
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div
        className={clsx(
          'border-t border-grey3 p-3 flex items-center gap-2.5',
          collapsed && 'justify-center'
        )}
      >
        <div className="w-9 h-9 rounded-full bg-cloud text-primary text-[13px] font-bold flex items-center justify-center flex-shrink-0">
          {initialsOf(user?.displayName)}
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-bold text-dark truncate">
              {user?.displayName}
            </div>
            <div className="text-[11px] text-light truncate">{user?.email}</div>
          </div>
        )}
        {!collapsed && (
          <button
            type="button"
            title="Log out"
            onClick={handleLogout}
            className="w-8 h-8 rounded-lg bg-grey4 text-light flex items-center justify-center hover:bg-dangerLight hover:text-danger transition-colors"
          >
            <LogOut className="w-[17px] h-[17px]" />
          </button>
        )}
      </div>
    </aside>
  );
};

export default AppSidebar;
